import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { extractIncomingLinkUrl, type IncomingLinkNodeData } from './extractIncomingLinkUrl.ts';
import { isCuratedIncomingLinkTypename } from './incomingLinkTypes.ts';

/**
 * The slice of a `graphStore_cypherQueryV2` result this reader walks. Any query that selects
 * its entity URLs through the catalogued `<Type>_<field>` fragments satisfies it; the
 * incoming-links and related-content queries both do.
 */
export type CuratedCypherQueryData = {
	readonly graphStore_cypherQueryV2: {
		readonly edges: ReadonlyArray<{
			readonly node: {
				readonly columns: ReadonlyArray<{
					readonly value: {
						readonly __typename?: string;
						readonly data?: IncomingLinkNodeData | null;
					} | null;
				}>;
			};
		}>;
	} | null;
};

/**
 * Reads the navigable URLs out of a `graphStore_cypherQueryV2` result. Every
 * incoming and related link is hydrated from its URL through ORS (see
 * `hydrateIncomingLinks`), so this is the whole of the graph's contribution: it
 * does not hydrate.
 *
 * URLs are de-duplicated in first-occurrence order, so link order follows the
 * query's result order and the same entity surfaced twice by the graph is listed
 * once.
 *
 * Two kinds of row produce nothing and are reported to monitoring once per call:
 * a node whose `__typename` is not in the curated catalog (its typenames are named
 * in the report, since that is the signal for extending
 * {@link isCuratedIncomingLinkTypename}'s catalog), and a curated node whose URL
 * field came back null.
 */
export const readIncomingLinkUrls = (data: CuratedCypherQueryData | null | undefined): string[] => {
	const urls: string[] = [];
	const seenUrls = new Set<string>();
	const uncataloguedTypenames = new Set<string>();
	let missingUrlCount = 0;

	// Work is bounded by the query's `first` (≤1000 rows, each with a single
	// column), so this flat/map chain is linear, not quadratic.
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
	const nodeData = (data?.graphStore_cypherQueryV2?.edges ?? [])
		.flatMap((edge) => edge?.node?.columns ?? [])
		.map((column) => column?.value?.data);

	nodeData.forEach((node) => {
		if (!node) {
			return;
		}

		const url = extractIncomingLinkUrl(node);
		if (url) {
			if (!seenUrls.has(url)) {
				seenUrls.add(url);
				urls.push(url);
			}
			return;
		}

		if (isCuratedIncomingLinkTypename(node.__typename)) {
			missingUrlCount += 1;
		} else {
			uncataloguedTypenames.add(node.__typename);
		}
	});

	if (uncataloguedTypenames.size > 0) {
		getMonitoringClient().submitError(
			new Error(
				`readIncomingLinkUrls: incoming link node type(s) missing from the catalog: ${[
					...uncataloguedTypenames,
				].join(', ')}`,
			),
			{ attribution: Attribution.INTEGRATIONS },
		);
	}

	if (missingUrlCount > 0) {
		getMonitoringClient().submitError(
			new Error(
				`readIncomingLinkUrls: ${missingUrlCount} curated incoming link node(s) had no url`,
			),
			{ attribution: Attribution.INTEGRATIONS },
		);
	}

	return urls;
};
